import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { KanbanBoard } from '../../components/work/KanbanBoard';
import { GoalActivityPanel } from '../../components/work/GoalActivityPanel';
import { useBoardView, useTeams } from '../../hooks/useWorkManagement';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { usePublishViewSnapshot } from '../../components/shogun/usePublishViewSnapshot';
import { boardRecords, scopeRecord } from '../../lib/view-snapshot-helpers';
import { activeTeamIdAtom, workItemSessionsAtom } from '../../stores/work/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export function TeamBoard() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const setLandingTeamId = useSetAtom(activeTeamIdAtom);
  const ticketMatch = useMatch('/work/teams/:teamId/tickets/:ticketId');

  const { data: board, isLoading, error } = useBoardView(teamId ?? null);
  const boardError = errorWithNothingCachedToShow(board, error);
  const { data: teams } = useTeams();
  const sessions = useAtomValue(workItemSessionsAtom);
  const { pathname } = useLocation();

  // Everything rendered goes to the docked agent — no sampling (app-shell ADR-014).
  const snapshot = useMemo(() => {
    if (!board || !teamId) return null;
    const team = teams?.find((t) => t.id === teamId);
    const { counts, visible } = boardRecords(board, sessions);
    return {
      route: pathname,
      context: {
        surface: 'work',
        primary_type: 'work_item',
        scope: [scopeRecord('team', teamId, team?.name ?? 'Team')],
        counts,
        visible,
        ...(ticketMatch?.params.ticketId
          ? {
              selected: {
                type: 'work_item',
                id: ticketMatch.params.ticketId,
                label:
                  visible.find((r) => r.id === ticketMatch.params.ticketId)?.label ?? 'Ticket',
              },
            }
          : {}),
      },
    };
  }, [board, teamId, teams, sessions, pathname, ticketMatch?.params.ticketId]);
  usePublishViewSnapshot(snapshot);

  useEffect(() => {
    if (teamId) setLandingTeamId(teamId);
  }, [teamId, setLandingTeamId]);

  if (!teamId) throw new Error('TeamBoard rendered without a teamId route param');

  return (
    <div className="flex min-h-0 flex-1" data-testid="team-board" data-team-id={teamId}>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {board && (
          <div className="border-b px-6 py-2 text-caption text-muted-foreground">
            <span data-testid="team-board-orphan-count" data-count={board.orphan_ticket_count}>
              {board.orphan_ticket_count} tickets with no project
            </span>
          </div>
        )}

        <div className="flex-1 overflow-auto p-6">
          {isLoading && (
            <LoadingState label="Loading board..." className="h-full" />
          )}

          {boardError && (
            <div className="flex h-full items-center justify-center">
              <div className="text-destructive">Failed to load board: {boardError.message}</div>
            </div>
          )}

          {board && <KanbanBoard board={board} />}
        </div>
      </div>

      {ticketMatch ? (
        <Outlet />
      ) : (
        <GoalActivityPanel
          teamId={teamId}
          goalId={null}
          onSelectGoal={(goalId) => navigate(`/work/teams/${teamId}/goals/${goalId}/projects`)}
        />
      )}
    </div>
  );
}
